import React, { useState, useMemo } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import { formatDateFR } from '../../shared/utils/helpers';
import StaffForm from './StaffForm';
import StaffPaymentForm from './StaffPaymentForm';
import StaffDetailModal from './StaffDetailModal';
import { 
  Plus, Edit2, Trash2, Search, Filter, Users, 
  DollarSign, CheckSquare, AlertTriangle, Eye, CreditCard 
} from 'lucide-react';

export default function StaffPage({ settings }) {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  
  const { data: staff, loading: loadingStaff, remove: removeStaff } = useCollection('staff');
  const { data: payments, loading: loadingPayments, remove: removePayment } = useCollection('staff_payments');
  const { data: commissions } = useCollection('staff_commissions');
  
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [paymentEmployee, setPaymentEmployee] = useState(null);
  const [detailEmployeeId, setDetailEmployeeId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [search, setSearch] = useState('');

  const todayStr = new Date().toISOString().split('T')[0];

  const handleDelete = async (id) => {
    if (!confirm(isAr 
      ? 'هل تريد حذف هذا الموظف؟ سيتم حذف كل سجل الدفعات أيضًا.' 
      : 'Supprimer cet employé? Tous les paiements seront aussi supprimés.'
    )) return;
    
    try {
      // 1. Delete employee
      await removeStaff(id);
      
      // 2. Delete all payments of this employee
      const myPayments = payments.filter(p => p.employeeId === id);
      for (const p of myPayments) {
        await removePayment(p.id);
      }
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  // Compute payment status per employee
  const computePaymentStatus = (employee) => {
    const today = new Date();
    const empPayments = payments.filter(p => p.employeeId === employee.id && p.type === 'salary');
    
    if (employee.salaryType === 'monthly') {
      // Check this month's salary
      const thisMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
      const lastPaid = empPayments
        .filter(p => p.paymentDate && p.paymentDate.startsWith(thisMonth))
        .sort((a, b) => (b.paymentDate || '').localeCompare(a.paymentDate || ''))[0];
      
      if (lastPaid) return { status: 'paid', label: isAr ? 'مدفوع' : 'Payé', color: 'green' };
      
      // Check if payment day passed
      const paymentDay = parseInt(employee.paymentDay) || 1;
      const todayDay = today.getDate();
      
      if (todayDay > paymentDay) {
        const daysLate = todayDay - paymentDay;
        return { 
          status: 'late', 
          label: isAr ? `متأخر ${daysLate} يوم` : `Retard ${daysLate}j`, 
          color: 'red',
          daysLate 
        };
      } else if (todayDay >= paymentDay - 3) {
        const daysUntil = paymentDay - todayDay;
        return { 
          status: 'soon', 
          label: daysUntil === 0 
            ? (isAr ? 'اليوم!' : "Aujourd'hui!") 
            : (isAr ? `بعد ${daysUntil} يوم` : `Dans ${daysUntil}j`),
          color: 'amber',
          daysUntil
        };
      }
      return { status: 'pending', label: isAr ? 'في الانتظار' : 'À payer', color: 'gray' };
    }
    
    // Daily: just show last payment date
    const lastPayment = empPayments
      .sort((a, b) => (b.paymentDate || '').localeCompare(a.paymentDate || ''))[0];
    if (lastPayment) {
      return { 
        status: 'recent', 
        label: isAr ? `آخر: ${formatDateFR(lastPayment.paymentDate)}` : `Dern.: ${formatDateFR(lastPayment.paymentDate)}`,
        color: 'gray'
      };
    }
    return { status: 'none', label: isAr ? 'لا توجد دفعات' : 'Aucun paiement', color: 'gray' };
  };

  // Stats
  const stats = useMemo(() => {
    const active = staff.filter(s => s.status === 'active').length;
    const monthlyTotal = staff
      .filter(s => s.status === 'active' && s.salaryType === 'monthly')
      .reduce((sum, s) => sum + (parseFloat(s.salaryAmount) || 0), 0);
    
    const currentMonth = new Date().toISOString().substring(0, 7);
    const paidThisMonth = payments
      .filter(p => p.type === 'salary' && p.paymentDate && p.paymentDate.startsWith(currentMonth))
      .reduce((sum, p) => sum + (p.amount || 0), 0);
    
    const advancesThisMonth = payments
      .filter(p => p.type === 'advance' && p.paymentDate && p.paymentDate.startsWith(currentMonth))
      .reduce((sum, p) => sum + (p.amount || 0), 0);
    
    // Today's attendance rate
    const activeStaff = staff.filter(s => s.status === 'active');
    let attendanceRate = null;
    if (activeStaff.length > 0) {
      const loggedToday = activeStaff.filter(s => s.attendance && s.attendance[todayStr]);
      if (loggedToday.length > 0) {
        const presentToday = loggedToday.filter(s => s.attendance[todayStr] === 'present' || s.attendance[todayStr] === 'late').length;
        attendanceRate = Math.round((presentToday / loggedToday.length) * 100);
      }
    }

    // Late payments
    const lateCount = staff.filter(s => s.status === 'active' && computePaymentStatus(s).status === 'late').length;
    
    return { 
      active, 
      total: staff.length, 
      monthlyTotal, 
      paidThisMonth, 
      advancesThisMonth,
      attendanceRate,
      lateCount
    };
  }, [staff, payments, todayStr]);

  // List of distinct roles present in DB
  const commissionStats = useMemo(() => {
    const list = commissions || [];
    const totalEarned = list.reduce((s, c) => s + (Number(c.amount) || 0), 0);
    const totalPaid = (payments || [])
      .filter(p => p.type === 'commission')
      .reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const pending = Math.max(0, totalEarned - totalPaid);
    const count = list.length;
    const avg = count ? Math.round(totalEarned / count) : 0;
    return { totalEarned, totalPaid, pending, count, avg };
  }, [commissions, payments]);

  const distinctRoles = useMemo(() => {
    const roles = staff.map(s => s.role).filter(Boolean);
    return Array.from(new Set(roles)).sort();
  }, [staff]);

  // Filter & Search
  const filtered = useMemo(() => {
    let result = staff;
    if (filterStatus !== 'all') result = result.filter(s => s.status === filterStatus);
    if (filterRole !== 'all') result = result.filter(s => s.role === filterRole);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(s =>
        (s.name || '').toLowerCase().includes(q) ||
        (s.role || '').toLowerCase().includes(q) ||
        (s.phone || '').toLowerCase().includes(q) ||
        (s.cin || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q)
      );
    }
    return [...result].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [staff, filterStatus, filterRole, search]);

  const colorBadgeBg = {
    green: 'bg-green-50 text-green-800 border-green-200',
    red: 'bg-red-50 text-red-800 border-red-200 animate-pulse',
    amber: 'bg-amber-50 text-amber-850 text-amber-800 border-amber-250 border-amber-200',
    gray: 'bg-gray-50 text-gray-800 border-gray-200'
  };

  const t = (fr, ar) => (isAr ? ar : fr);

  if (loadingStaff || loadingPayments) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={isAr ? 'rtl' : 'ltr'}>
      
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">
            👥 {t('Gestion du Personnel', 'إدارة الموظفين')}
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            {t('Suivi de contrat, paie, présences et évaluations de l\'équipe', 'متابعة عقود الموظفين، الأجور، الحضور وتقييم أداء فريق العمل')}
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl font-bold transition shadow-md shadow-blue-200 text-sm"
        >
          <Plus className="w-4 h-4" />
          <span>{t('Nouvel employé', 'موظف جديد')}</span>
        </button>
      </div>
      
      {/* Premium Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        
        <div className="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl shrink-0">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{t('Effectif Actif', 'الموظفين النشطين')}</div>
            <div className="text-2xl font-black text-gray-900 mt-0.5">{stats.active}</div>
            <div className="text-[10px] text-gray-500 font-semibold mt-0.5">
              {t(`sur ${stats.total} au total`, `من أصل ${stats.total} موظف`)}
            </div>
          </div>
        </div>

        <div className="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-xl shrink-0">
            <DollarSign className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{t('Masse salariale', 'كتلة الأجور')}</div>
            <div className="text-xl font-black text-gray-900 mt-0.5 font-mono truncate">
              {stats.monthlyTotal.toLocaleString()} <span className="text-xs font-bold text-gray-550 text-gray-500">MAD</span>
            </div>
            <div className="text-[10px] text-gray-500 font-semibold mt-0.5">
              {t('Mensuelle (fixes)', 'الالتزامات الشهرية')}
            </div>
          </div>
        </div>

        <div className="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-green-50 text-green-600 flex items-center justify-center text-xl shrink-0">
            <CheckSquare className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{t('Présences du jour', 'حضور اليوم')}</div>
            <div className="text-2xl font-black text-gray-900 mt-0.5">
              {stats.attendanceRate !== null ? `${stats.attendanceRate}%` : '--'}
            </div>
            <div className="text-[10px] text-gray-500 font-semibold mt-0.5">
              {stats.attendanceRate !== null ? t('Taux enregistré aujourd\'hui', 'نسبة تسجيل حضور اليوم') : t('Aucun pointage', 'لا يوجد تسجيل حضور بعد')}
            </div>
          </div>
        </div>

        <div className="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-red-50 text-red-600 flex items-center justify-center text-xl shrink-0">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{t('Salaires en Retard', 'أجور متأخرة')}</div>
            <div className="text-2xl font-black text-red-600 mt-0.5">{stats.lateCount}</div>
            <div className="text-[10px] text-red-500 font-bold mt-0.5">
              {t('Employés à payer urgemment', 'موظفين بانتظار الصرف')}
            </div>
          </div>
        </div>

      </div>

      {/* Commission dashboard */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="bg-white p-3 rounded-2xl border border-gray-200 shadow-sm">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{t('Total commissions', 'إجمالي العمولات')}</div>
          <div className="text-lg font-black text-emerald-700 font-mono mt-0.5">{commissionStats.totalEarned.toLocaleString()} <span className="text-[10px] text-gray-400">MAD</span></div>
        </div>
        <div className="bg-white p-3 rounded-2xl border border-gray-200 shadow-sm">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{t('En attente', 'قيد الانتظار')}</div>
          <div className="text-lg font-black text-amber-600 font-mono mt-0.5">{commissionStats.pending.toLocaleString()} <span className="text-[10px] text-gray-400">MAD</span></div>
        </div>
        <div className="bg-white p-3 rounded-2xl border border-gray-200 shadow-sm">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{t('Payées', 'مدفوعة')}</div>
          <div className="text-lg font-black text-blue-600 font-mono mt-0.5">{commissionStats.totalPaid.toLocaleString()} <span className="text-[10px] text-gray-400">MAD</span></div>
        </div>
        <div className="bg-white p-3 rounded-2xl border border-gray-200 shadow-sm">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{t('Contrats commissionnés', 'عقود بعمولة')}</div>
          <div className="text-lg font-black text-gray-900 font-mono mt-0.5">{commissionStats.count}</div>
        </div>
        <div className="bg-white p-3 rounded-2xl border border-gray-200 shadow-sm">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{t('Moyenne / contrat', 'المعدل/عقد')}</div>
          <div className="text-lg font-black text-purple-700 font-mono mt-0.5">{commissionStats.avg.toLocaleString()} <span className="text-[10px] text-gray-400">MAD</span></div>
        </div>
      </div>

      {/* Modern Filter panel */}
      <div className="bg-white p-4 border border-gray-200 rounded-2xl shadow-sm flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
        
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('Rechercher par nom, téléphone, CIN...', 'البحث بالاسم، الهاتف، بطاقة التعريف...')}
            className={`w-full py-2.5 border border-gray-300 rounded-xl text-xs outline-none focus:ring-2 focus:ring-blue-500 ${
              isAr ? 'pr-3 pl-10' : 'pl-10 pr-3'
            }`}
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2.5 items-center">
          
          {/* Role selector dropdown */}
          <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-300 px-3 py-1.5 rounded-xl">
            <Filter className="w-3.5 h-3.5 text-gray-500" />
            <select
              value={filterRole}
              onChange={e => setFilterRole(e.target.value)}
              className="bg-transparent text-xs font-bold text-gray-700 outline-none cursor-pointer"
            >
              <option value="all">{t('Tous les postes', 'كل الوظائف')}</option>
              {distinctRoles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          {/* Status buttons */}
          <div className="flex bg-gray-50 border border-gray-300 rounded-xl overflow-hidden p-0.5">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-black transition-all ${filterStatus === 'all' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              {t('Tous', 'الكل')}
            </button>
            <button
              onClick={() => setFilterStatus('active')}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-black transition-all ${filterStatus === 'active' ? 'bg-green-600 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              🟢 {t('Actifs', 'النشطين')}
            </button>
            <button
              onClick={() => setFilterStatus('inactive')}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-black transition-all ${filterStatus === 'inactive' ? 'bg-gray-650 bg-gray-600 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              🔴 {t('Inactifs', 'غير النشطين')}
            </button>
          </div>

        </div>

      </div>

      {/* Staff Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed border-gray-250">
          <div className="text-6xl mb-3">👥</div>
          <p className="text-gray-500 font-bold text-sm">
            {staff.length === 0
              ? t('Aucun employé enregistré pour le moment', 'لا يوجد أي موظف مسجل حالياً')
              : t('Aucun employé ne correspond aux critères', 'لا توجد نتائج تطابق خيارات البحث')}
          </p>
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl transition"
          >
            ➕ {t('Ajouter un employé', 'إضافة موظف جديد')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(emp => {
            const paymentStatus = computePaymentStatus(emp);
            const isInactive = emp.status === 'inactive';
            const badgeBg = colorBadgeBg[paymentStatus.color] || colorBadgeBg.gray;
            
            return (
              <div
                key={emp.id}
                className={`bg-white border rounded-2xl p-5 shadow-sm hover:shadow-md transition-all flex flex-col justify-between gap-4 ${
                  isInactive ? 'opacity-65 border-gray-200' : 'border-gray-200'
                }`}
              >
                
                {/* Employee Header */}
                <div className="flex items-start gap-4">
                  
                  {/* Photo or Initials Avatar */}
                  {emp.photo ? (
                    <img 
                      src={emp.photo} 
                      alt={emp.name} 
                      className="w-14 h-14 rounded-full object-cover ring-2 ring-gray-100 shadow-sm shrink-0" 
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-black flex items-center justify-center text-xl shadow-sm shrink-0">
                      {emp.name.charAt(0).toUpperCase()}
                    </div>
                  )}

                  {/* Name and Role */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-bold text-gray-900 text-sm truncate">{emp.name}</h3>
                      <span className={`w-2 h-2 rounded-full shrink-0 ${isInactive ? 'bg-gray-400' : 'bg-green-500'}`} />
                    </div>
                    
                    <div className="inline-block px-2.5 py-0.5 bg-blue-50 border border-blue-100 text-[10px] font-bold text-blue-600 rounded-full mt-1">
                      {emp.role || t('Poste indéfini', 'بدون وظيفة')}
                    </div>
                  </div>

                </div>

                {/* Details */}
                <div className="space-y-1.5 text-xs text-gray-600 border-t border-gray-100 pt-3">
                  {emp.phone && (
                    <div className="flex items-center gap-2">
                      <span className="opacity-70">📞</span>
                      <span className="font-mono font-medium">{emp.phone}</span>
                    </div>
                  )}
                  {emp.cin && (
                    <div className="flex items-center gap-2">
                      <span className="opacity-70">🪪</span>
                      <span className="font-mono font-medium">{emp.cin}</span>
                    </div>
                  )}
                  {emp.hireDate && (
                    <div className="flex items-center gap-2">
                      <span className="opacity-70">📅</span>
                      <span>{t('Embauche: ', 'توظيف: ')}<span className="font-semibold">{formatDateFR(emp.hireDate)}</span></span>
                    </div>
                  )}
                </div>

                {/* Salary Info Box */}
                <div className="p-3 bg-amber-50/50 border border-amber-200 rounded-xl flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-[9px] font-bold text-amber-700 uppercase tracking-wide">
                      {emp.salaryType === 'monthly' ? t('📆 Mensuel', '📆 راتب شهري') : t('☀️ Journalier', '☀️ راتب يومي')}
                    </div>
                    {emp.salaryType === 'monthly' && emp.paymentDay && (
                      <div className="text-[9px] text-amber-600 font-semibold mt-0.5">
                        {t(`Le ${emp.paymentDay} du mois`, `يوم الصرف: ${emp.paymentDay}`)}
                      </div>
                    )}
                  </div>
                  <div className="text-sm font-black text-amber-900 font-mono text-right shrink-0">
                    {emp.salaryAmount} <span className="text-[10px] font-normal text-amber-700">MAD</span>
                  </div>
                </div>

                {/* Payment Badge Status */}
                {!isInactive && (
                  <div className={`px-2 py-1.5 rounded-xl text-center border text-[10px] font-bold tracking-wide ${badgeBg}`}>
                    {paymentStatus.status === 'paid' && '✅ '}
                    {paymentStatus.status === 'late' && '🚨 '}
                    {paymentStatus.status === 'soon' && '⏰ '}
                    {paymentStatus.status === 'pending' && '⏳ '}
                    {paymentStatus.label.toUpperCase()}
                  </div>
                )}

                {/* Actions Grid */}
                <div className="flex gap-2 border-t border-gray-100 pt-3">
                  
                  {/* Pay button */}
                  <button
                    onClick={() => setPaymentEmployee(emp)}
                    className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition shadow"
                    title={t('Enregistrer un versement', 'تسجيل دفعة جديدة')}
                  >
                    <span>💰</span>
                    <span>{t('Payer', 'صرف')}</span>
                  </button>

                  {/* Profile details */}
                  <button
                    onClick={() => setDetailEmployeeId(emp.id)}
                    className="px-2.5 py-2 text-blue-600 border border-blue-200 hover:bg-blue-50 rounded-xl transition"
                    title={t('Détails & documents & planning', 'التفاصيل والملف')}
                  >
                    <Eye className="w-4 h-4" />
                  </button>

                  {/* Edit */}
                  <button
                    onClick={() => { setEditing(emp); setShowForm(true); }}
                    className="px-2.5 py-2 text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-xl transition"
                    title={t('Modifier', 'تعديل')}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>

                  {/* Delete */}
                  <button
                    onClick={() => handleDelete(emp.id)}
                    className="px-2.5 py-2 text-red-650 border border-red-200 hover:bg-red-50 rounded-xl transition"
                    title={t('Supprimer', 'حذف')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                </div>

              </div>
            );
          })}
        </div>
      )}

      {/* Modal containers */}
      {showForm && (
        <StaffForm
          employee={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}
      {paymentEmployee && (
        <StaffPaymentForm
          employee={paymentEmployee}
          settings={settings}
          onClose={() => setPaymentEmployee(null)}
        />
      )}
      {detailEmployeeId && (
        <StaffDetailModal
          employeeId={detailEmployeeId}
          settings={settings}
          onClose={() => setDetailEmployeeId(null)}
        />
      )}

    </div>
  );
}
